import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';

function colClass(columns) {
  const md = columns?.md || 1;
  if (md >= 3) return 'col-12 col-md-4';
  if (md === 2) return 'col-12 col-md-6';
  return 'col-12';
}

function FieldControl({ field, value, onChange, readOnly }) {
  const id = `opd-${field.id}`;
  const disabled = readOnly;

  const setValue = (next) => onChange(field.id, next);

  switch (field.type) {
    case 'textarea':
      return (
        <Form.Control
          as="textarea"
          rows={3}
          id={id}
          value={value || ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      );
    case 'number':
      return (
        <Form.Control
          type="number"
          id={id}
          value={value ?? ''}
          onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      );
    case 'date':
      return (
        <Form.Control
          type="date"
          id={id}
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      );
    case 'time':
      return (
        <Form.Control
          type="time"
          id={id}
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      );
    case 'select':
      return (
        <Form.Select
          id={id}
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        >
          <option value="">Select…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Form.Select>
      );
    case 'radio':
      return (
        <div className="d-flex flex-wrap gap-3">
          {(field.options || []).map((opt) => (
            <Form.Check
              key={opt}
              type="radio"
              id={`${id}-${opt}`}
              name={id}
              label={opt}
              checked={value === opt}
              onChange={() => setValue(opt)}
              disabled={disabled}
            />
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <Form.Check
          type="checkbox"
          id={id}
          label={field.checkboxLabel || 'Yes'}
          checked={!!value}
          onChange={(e) => setValue(e.target.checked)}
          disabled={disabled}
        />
      );
    case 'checkboxGroup': {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        if (disabled) return;
        if (selected.includes(opt)) {
          setValue(selected.filter((x) => x !== opt));
        } else {
          setValue([...selected, opt]);
        }
      };
      return (
        <div className="row g-2">
          {(field.options || []).map((opt) => (
            <div key={opt} className="col-12 col-md-6">
              <Form.Check
                type="checkbox"
                id={`${id}-${opt}`}
                label={opt}
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      );
    }
    default:
      return (
        <Form.Control
          type="text"
          id={id}
          value={value || ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          style={{ fontSize: '16px' }}
        />
      );
  }
}

function OpdDynamicForm({ schema, formData, onFieldChange, readOnly, sectionNavRef }) {
  const sections = schema?.sections || [];

  const handleChange = useCallback(
    (fieldId, next) => {
      if (readOnly) return;
      onFieldChange(fieldId, next);
    },
    [onFieldChange, readOnly]
  );

  const navItems = useMemo(
    () => sections.map((s) => ({ id: s.id, title: s.title || s.id })),
    [sections]
  );

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(`opd-section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!sections.length) {
    return <p className="text-muted">No form fields in this template.</p>;
  }

  return (
    <div className="opd-dynamic-form">
      <div className="opd-section-nav mb-3" ref={sectionNavRef}>
        <div className="d-flex flex-nowrap gap-2 overflow-auto pb-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="btn btn-sm btn-outline-secondary text-nowrap"
              onClick={() => scrollToSection(item.id)}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>

      {sections.map((section) => (
        <div
          key={section.id}
          id={`opd-section-${section.id}`}
          className="card mb-3 shadow-sm"
        >
          <div className="card-header fw-semibold">{section.title || section.id}</div>
          <div className="card-body">
            <div className="row g-3">
              {(section.fields || []).map((field) => (
                <div key={field.id} className={colClass(section.columns)}>
                  <Form.Group>
                    <Form.Label className="mb-1">{field.label}</Form.Label>
                    <FieldControl
                      field={field}
                      value={formData[field.id]}
                      onChange={handleChange}
                      readOnly={readOnly}
                    />
                  </Form.Group>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default OpdDynamicForm;
